import { FabricStatus } from '@/types/fabric'
import { ShareType } from '@/types/monitor'
import { z } from 'zod'
// 面料类型选项：用于区分面料的供应状态
export const FABRIC_TYPE_OPTIONS = [
  { label: '现货', value: '1' },
  { label: '期货', value: '2' },
]
export const createNumberStringSchema = (fieldName: string = '该字段', allowEmpty: boolean = true, customMessage?: string) => {
  return z.string().refine(
    (val) => {
      // 如果允许为空且值为空，则通过校验
      if (allowEmpty && val === '') return true

      // 如果不允许为空且值为空，则校验失败
      if (!allowEmpty && val === '') return false

      // 检查是否为有效数字且大于0
      const num = parseFloat(val)
      return !isNaN(num) && num > 0
    },
    {
      message: customMessage || `${fieldName}${allowEmpty ? '必须为空或大于0的数字' : '必须大于0'}`,
    },
  )
}
const nullableNumber = (max: number, message: string) =>
  z.custom(
    (val) => {
      if (val) {
        const num = parseFloat(val)
        return !isNaN(num) && num < max && num > 0
      }
      return true
    },
    {
      message: message,
    },
  )
export const fabricSchema = z.object({
  // 基础信息
  fabricId: z.string().optional(), // 面料ID
  fabricName: z.string().refine(
    (val) => {
      // 检查长度是否在5-30之间
      const lengthValid = val.length >= 5 && val.length <= 30
      // 检查是否包含中文
      const hasChinese = /[\u4e00-\u9fa5]/.test(val)
      return lengthValid && hasChinese
    },
    (val) => {
      const hasChinese = /[\u4e00-\u9fa5]/.test(val)

      if (val.length < 5) {
        if (!hasChinese) {
          return {
            message: '面料名称不少于5个字且需包含中文',
          }
        } else {
          return {
            message: '面料名称不少于5个字',
          }
        }
      } else if (val.length > 30) {
        if (!hasChinese) {
          return {
            message: '面料名称不多于30个字且需包含中文',
          }
        }
        return {
          message: '面料名称不多于30个字',
        }
      } else {
        return {
          message: '面料名称需包含中文',
        }
      }
    },
  ), // 面料名称
  articleNo: z.string().min(1, '请输入货号').max(64, '货号不能超过64个字符'), // 货号
  city: z.string().optional(), // 发货地
  fabricType: z.enum(FABRIC_TYPE_OPTIONS.map((item) => item.value) as [string], {
    message: '请选择面料类型', // 自定义必填提示
  }), // 面料类型
  status: z.nativeEnum(FabricStatus).default(FabricStatus.NotPromoted),
  // 拆分价格字段为值和单位
  priceMin: createNumberStringSchema('面料售价', false), // 面料售价值
  priceMax: createNumberStringSchema('面料售价', false), // 面料售价值
  priceUnit: z.string().default('米'), // 面料售价单位
  // 拆分起批量字段为值和单位
  bulkOrder: createNumberStringSchema('起批量'), // 合并后的起批量字段，用于API提交
  // 最大订购数量
  maxOrderQuantity: createNumberStringSchema(), // 起批量最大阈值（超过面议）
  remark: z.string().max(500, '备注不能超过500个字符').nullable().optional(), // 备注（选填）
  // 图片信息
  fabricDetailPic: z
    .array(z.string())
    .min(1, '请上传面料细节图')
    .max(5, '面料细节图最多上传5张')
    .refine((val) => val[0] && val[0].trim() !== '', {
      message: '请上传面料首图',
    }), // 面料细节图
  fabricVideo: z.array(z.string()).optional(), // 面料视频
  fabricParticularsPic: z.array(z.string()).optional(), // 面料细节图
  fabricColorPic: z.array(z.string()).max(50, '主推色最多上传50张'), // 主推色
  fabricSpecificationPic: z
    .array(
      z.object({
        name: z.string(),
        url: z.string(),
        colorNum: z.string().optional(),
      }),
    )
    .min(1, '请上传主推色')
    .max(5, '面料色卡最多上传5张'), // 面料色卡
  garmentDisplayList: z
    .array(
      z.object({
        styleId: z.string().optional(),
        garmentDisplayPic: z.string().optional(),
      }),
    )
    .optional(), // 成衣展示图（选填）

  // 面料属性
  category: z.string().min(1, '请选择品类'), // 品类
  pattern: z.string().nullable().optional(), // 图案（选填）
  suitableSeason: z.array(z.string()).min(1, '请选择适用季节'), // 适用季节
  craft: z.array(z.string()).optional(), // 工艺
  compositionList: z
    .array(
      z.object({
        name: z.string().min(1, '请选择成分'),
        composition: z
          .union([z.string().optional(), z.coerce.number().gt(0, '含量不能小于0').lte(100, '含量不能超过100%').transform(String)])
          .optional(),
      }),
    )
    .min(1, '请选择成分'), // 成分
  isComposition: z.boolean().default(false),
  mainPurpose: z.string().nullable().optional(), // 用途
  purpose: z.array(z.string()).min(1, '请选择用途'), // 用途
  // style: z.string().optional(), // 适用风格
  // 拆分克重字段为值和单位
  gramWeightMin: z.string().optional(), // 面料克重
  gramWeightMax: z.string().optional(), // 面料克重
  fabricWidth: nullableNumber(500, '门幅必须在0-500之间'),
  fabricFeature: z.string().max(100, '特性不能超过100个字符').nullable().optional(), // 特性（选填）
  warpShrinkage: nullableNumber(100, '纬缩必须在0-100之间'), // 经向缩水率（%）
  weftShrinkage: nullableNumber(100, '经缩必须在0-100之间'), // 纬向缩水率（%）
  threadCount: nullableNumber(1000, '支数必须在0-1000之间'), // 纱支数（支）
  fabricYield: nullableNumber(100, '出布率必须在0-100之间'), // 出布率（%）
  colorFastness: z.string().nullable().optional(), // 色牢度
  burstingStrength: z.string().or(z.null()).optional(), // 顶破强度（选填）
  functionalityList: z
    .array(
      z.object({
        name: z.string(),
        remark: z.string().nullable().optional(),
      }),
    )
    .optional(), // 功能性（选填）
  // 感官指数
  thickness: z.string().nullable().optional(), // 厚度（选填）
  elasticity: z.string().nullable().optional(), // 弹力（选填）
  ventilate: z.string().nullable().optional(), // 透气性（选填）
  hygroscopic: z.string().nullable().optional(), // 吸湿性（选填）
  softness: z.string().nullable().optional(), // 柔软度（选填）
  drapeability: z.string().nullable().optional(), // 悬垂性（选填）
  thermalInsulation: z.string().nullable().optional(), // 保暖性（选填）

  // 其他字段
  teamId: z.number(), // 团队ID
  teamName: z.string().optional(), // 团队名称
  fabricPromotionProjectList: z
    .array(
      z.object({
        status: z.number(),
      }),
    )
    .nullable()
    .optional(),
})

export type FabricSchema = z.infer<typeof fabricSchema>

export const shopGroupSchema = z.object({
  createdAt: z.string().optional(), // 创建时间
  groupId: z.string().optional(), // 分组ID
  groupIdList: z.array(z.string()).optional(), // 店铺ID列表
  groupName: z.string().min(1, '请输入分组名称').max(15, '分组名称不能超过15个字'), // 分组名称
  groupType: z.number().default(1), // 分组类型
  monitorIdList: z.array(z.string()).optional(), // 监控ID列表
  monitorNum: z.number().optional(), // 监控数量
  order: z.number().optional(), // 排序
  share: z.nativeEnum(ShareType).default(ShareType.All), // 共享设置
  shareUserIdList: z.array(z.number()).nullable().optional(), // 共享用户ID列表
  shareUserNum: z.number().nullable().optional(), // 共享用户数量
  teamId: z.number().optional(), // 团队ID
  userId: z.number().optional(), // 用户ID
  userName: z.string().nullable().optional(), // 用户名称
})

export type ShopGroupSchema = z.infer<typeof shopGroupSchema>

export const companySchema = z.object({
  sellerLogo: z.array(z.string()).min(1, '请上传LOGO'), // logo
  sellerName: z.string().min(1, '请输入商家名'), // 商家名
  primaryProducts: z.array(z.string()).min(1, '请选择主营产品'), // 主营产品
  customAddress: z.array(z.string()).min(1, '请选择地址'), // 地址
  address: z.string(), // 地址
  city: z.string(), // 地址
  profile: z.string().min(1, '请输入简介'), // 简介
  establishAt: z.string().optional(), // 创立时间
  factoryArea: z.string().optional(), // 工厂面积
  staffNum: z.string().optional(), // 员工人数
  contactPerson: z.string().min(1, '请输入联系人'), // 联系人
  contactPhone: z
    .string()
    .min(1, '请输入联系方式')
    .regex(/^1[3-9]\d{9}$/, '请输入有效的手机号'), // 联系方式
  wechatQrCode: z.array(z.string()).min(1, '请上传微信二维码'), // 微信二维码
  introductionVideo: z.array(z.string()), // 介绍视频
})

export type CompanySchema = z.infer<typeof companySchema>
